/**
 * readAloud/player — one readout at a time, across the whole app.
 *
 * A module singleton with a `useSyncExternalStore` subscription, the same idiom
 * the delivery ledger uses (services/messageDelivery): the transcript renders
 * hundreds of message rows and each one only needs to know "am I the one being
 * read?", so a single store beats per-row state and makes "one readout at a
 * time" true by construction rather than by convention.
 *
 * WHAT MAKES IT FEEL FAST. The server splits a message into sentence-bounded
 * segments and renders one per call, with a SHORT opening segment so the first
 * sound arrives in about a second. This player plays segment 0 the moment it
 * arrives, fetches segment 1 while 0 is playing AND assigns that URL to the
 * idle element the instant it lands — so the next segment's BYTES are already
 * downloading while the current one sounds, and the seam between them is a
 * `play()` on a buffered element rather than a cold ~1 MB download. (Until
 * 2026-09-10 this file's header claimed that buffering while `el.src` was
 * assigned only after the previous segment ended; the claim is now the code.)
 *
 * THE READER IS IN CHARGE (2026-09-10, Miles): while a readout is going the row
 * carries a player — previous / next section, pause and play, restart, and a
 * speed slider that moves in 10-percentage-point steps from 50 % to 200 %,
 * remembered for next time. Every one of those is a command on the run below:
 * a skip re-uses this run's URL cache, so moving back and forth never re-renders
 * audio and never costs a second cent.
 *
 * THE iOS UNLOCK IS LOAD-BEARING. Mobile Safari only lets an Audio element play
 * if it was played during a user gesture — and the first fetch happens after the
 * click, which breaks the gesture chain. So both elements are primed with a
 * silent clip synchronously inside the click, before anything is awaited. Take
 * that away and the control works everywhere except a phone, which is where it
 * matters most.
 *
 * Deliberately NOT stopped when the tab is hidden: somebody having a message
 * read to them switches tabs and keeps listening. It stops on a second click,
 * on a thread switch, and when the surface unmounts.
 */

import { fetchReadAloudSegment, type ReadAloudSegment } from './client';
import { cancelDeviceVoice, deviceVoiceAvailable, speakWithDeviceVoice } from './deviceVoice';
import { readAloudPlaybackFailedNotice } from './readAloudCopy';

export type ReadAloudState = 'idle' | 'loading' | 'playing' | 'paused';

export interface ReadAloudSnapshot {
  /** The message being read, as `${threadId}|${messageId}`, or null. */
  key: string | null;
  state: ReadAloudState;
  /** Which segment is sounding, and how many there are (for the label). */
  segment: number;
  segmentCount: number;
  /**
   * How far through the WHOLE readout the voice is, 0..1 (2026-09-10).
   *
   * The player used to show "2 / 5" — our server-side chunking, leaking into
   * the reader's face. Nobody asked to hear section two; they asked how far
   * along it is. This is (segments done + how far through this one) / total,
   * which needs no durations up front and never lies about a total we do not
   * know yet.
   */
  progress: number;
  /** Seconds of speech played so far, for the clock the reader actually reads. */
  elapsedSeconds: number;
  /**
   * The voice has run out of ready audio mid-readout and the next part is still
   * being made. A seam should be silent for a moment, not a mystery: when this
   * is true the bar says so instead of sitting there looking finished.
   */
  preparingNext: boolean;
  /** Playback speed, 0.5–2.0 in 0.1 steps — the viewer's, remembered. */
  rate: number;
  /** D9 copy for a readout that did not happen, or that dropped out. */
  notice: string | null;
  /** The message the notice belongs to, so only that row shows it. */
  noticeKey: string | null;
}

export interface ReadAloudTarget {
  threadId: string;
  messageId: string;
  /** What the reader is looking at: the device-voice text, and the store-miss fallback. */
  text: string;
  language?: string;
}

/** Speed bounds, in the reader's own terms: 50 % … 200 %, ten points at a time. */
export const READ_ALOUD_MIN_RATE = 0.5;
export const READ_ALOUD_MAX_RATE = 2;
export const READ_ALOUD_RATE_STEP = 0.1;
export const READ_ALOUD_RATE_KEY = 'prest0n:readAloud:rate';

function clampRate(rate: number): number {
  if (!Number.isFinite(rate)) return 1;
  const snapped = Math.round(rate * 10) / 10;
  return Math.min(READ_ALOUD_MAX_RATE, Math.max(READ_ALOUD_MIN_RATE, snapped));
}

function storedRate(): number {
  try {
    const raw = window.localStorage.getItem(READ_ALOUD_RATE_KEY);
    if (!raw) return 1;
    return clampRate(Number(raw));
  } catch {
    return 1;
  }
}

const IDLE: ReadAloudSnapshot = {
  key: null,
  state: 'idle',
  segment: 0,
  segmentCount: 0,
  progress: 0,
  elapsedSeconds: 0,
  preparingNext: false,
  rate: 1,
  notice: null,
  noticeKey: null,
};

/** A 0-sample WAV. Primes an Audio element inside a gesture without a sound. */
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgLsAAAB3AQACABAAZGF0YQAAAAA=';

let snapshot: ReadAloudSnapshot = { ...IDLE, rate: storedRate() };
const listeners = new Set<() => void>();

/** Bumped on every start and every stop; async work from an older run is dropped. */
let generation = 0;

let elements: HTMLAudioElement[] = [];
let unlocked = false;

/** What ended a segment's playback — 'seek' and 'stopped' are the reader's own doing. */
type PlayOutcome = 'ended' | 'failed' | 'seek' | 'stopped';

interface ActiveRun {
  run: number;
  key: string;
  target: ReadAloudTarget;
  total: number;
  /** The segment the reader is on. A skip writes it; the loop reads it every turn. */
  index: number;
  /** This run's segments by index — a skip back plays from here, never a re-render. */
  cache: Map<number, ReadAloudSegment>;
  /** The element carrying the segment that is sounding right now. */
  el: HTMLAudioElement | null;
  /** Ends the sounding segment with the given outcome (a skip, a stop). */
  interrupt: ((how: PlayOutcome) => void) | null;
  /** Segments whose transport already failed once — the second failure is real. */
  retried: Set<number>;
  /** Seconds of speech finished before the segment now sounding (the clock's base). */
  playedSeconds: number;
  /**
   * Fetches already in the air, by index. Two callers now ask for the same
   * segment — the early kickoff below and the play loop's own look-ahead — and
   * a second request is not merely wasted: an uncached segment is a PAID
   * render, so a duplicate would bill the account twice for one piece of audio.
   */
  inFlight: Map<number, Promise<ReadAloudSegment | null>>;
}

let active: ActiveRun | null = null;

function publish(next: Partial<ReadAloudSnapshot>): void {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((l) => {
    try {
      l();
    } catch {
      /* a bad subscriber must not stop playback */
    }
  });
}

export function subscribeReadAloud(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function readAloudSnapshot(): ReadAloudSnapshot {
  return snapshot;
}

export function readAloudKey(threadId: string | null | undefined, messageId: string): string {
  return `${threadId || ''}|${messageId}`;
}

function audioElements(): HTMLAudioElement[] {
  if (typeof document === 'undefined') return [];
  if (elements.length === 0) {
    elements = [new Audio(), new Audio()];
    for (const el of elements) {
      el.preload = 'auto';
      // Keeps iOS from taking the readout fullscreen or over the video surface.
      el.setAttribute('playsinline', 'true');
    }
  }
  return elements;
}

/**
 * Prime both elements inside the click. Called synchronously from `toggle`,
 * before any await — that is the whole point.
 */
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  for (const el of audioElements()) {
    try {
      el.src = SILENT_WAV;
      const p = el.play();
      if (p && typeof p.then === 'function') p.then(() => el.pause()).catch(() => undefined);
    } catch {
      /* an element that refuses to prime still tries to play later */
    }
  }
}

function halt(): void {
  cancelDeviceVoice();
  for (const el of elements) {
    try {
      el.pause();
      // Drop the source too: a paused element keeps downloading the rest.
      el.removeAttribute('src');
      el.load();
    } catch {
      /* already gone */
    }
  }
}

export function stopReadAloud(): void {
  generation += 1;
  const run = active;
  active = null;
  run?.interrupt?.('stopped');
  halt();
  publish({ key: null, state: 'idle', segment: 0, segmentCount: 0, progress: 0, elapsedSeconds: 0, preparingNext: false });
}

/**
 * A thread switch ends a readout that belonged to the thread being left.
 *
 * A missing id stops NOTHING: the host calls this with the previous thread,
 * which is null on the first render, and a readout must not be cancelled by the
 * surface simply mounting.
 */
export function stopReadAloudForThread(threadId: string | null | undefined): void {
  const id = String(threadId || '').trim();
  if (!id || !snapshot.key) return;
  if (snapshot.key.startsWith(`${id}|`)) stopReadAloud();
}

export function dismissReadAloudNotice(): void {
  if (snapshot.notice === null && snapshot.noticeKey === null) return;
  publish({ notice: null, noticeKey: null });
}

// ── The reader's controls (2026-09-10) ──────────────────────────────────────

/** Hold the readout where it is. The bytes stay buffered; play() resumes it. */
export function pauseReadAloud(): void {
  if (!active || snapshot.state !== 'playing' || !active.el) return;
  try {
    active.el.pause();
  } catch {
    /* an element that will not pause is already gone */
  }
  publish({ state: 'paused' });
}

/** Carry on from exactly where the pause landed. */
export function resumeReadAloud(): void {
  if (!active || snapshot.state !== 'paused' || !active.el) return;
  publish({ state: 'playing' });
  try {
    const p = active.el.play();
    if (p && typeof p.catch === 'function') p.catch(() => undefined);
  } catch {
    /* the loop's own error handler owns the failure path */
  }
}

/** One control for the reader: play what is paused, pause what is playing. */
export function toggleReadAloudPause(): void {
  if (snapshot.state === 'paused') resumeReadAloud();
  else pauseReadAloud();
}

/** Jump to a segment by index — the loop plays it from this run's cache. */
function goToSegment(index: number): void {
  if (!active) return;
  const target = Math.min(Math.max(0, index), Math.max(0, active.total - 1));
  active.index = target;
  // A skip is a fresh chance for a segment whose transport failed once.
  active.retried.delete(target);
  // The clock follows the jump instead of carrying the old position forward.
  // Segments before this one are counted whole; anything already banked from
  // segments at or after it is dropped.
  active.playedSeconds = 0;
  publish({ segment: target, state: 'playing', progress: target / Math.max(1, active.total), elapsedSeconds: 0 });
  active.interrupt?.('seek');
}

/**
 * Move between the message's spoken segments: -1 back, +1 forward.
 *
 * PARKED, NOT DELETED (Miles, 2026-09-10: "remove the sections for smaller
 * items — pin this for later because this is not a good user experience right
 * now"). No control calls this today. Segments are how the SERVER chunks text
 * for the voice, not a thing a reader has a mental model of: on the ordinary
 * message they were one, so the two buttons sat permanently dead and the
 * counter read "1 / 1". The mechanism stays here, tested and ready, for
 * whenever there is a genuinely good answer for navigating a long readout —
 * which will more likely be scrubbing a timeline than stepping our chunks.
 */
export function seekReadAloudSegment(delta: number): void {
  if (!active) return;
  goToSegment(active.index + delta);
}

/** Back to the beginning of the message, from the top. */
export function restartReadAloud(): void {
  if (!active) return;
  goToSegment(0);
}

/**
 * Speed, in the reader's own units: 50 % … 200 %, snapped to ten points. The
 * choice is remembered for the next readout (per browser, per device) and is
 * applied to the sounding element at once, so the slider moves the voice under
 * the finger rather than at the next section.
 */
export function setReadAloudRate(rate: number): void {
  const next = clampRate(rate);
  if (next === snapshot.rate) return;
  for (const el of elements) {
    try {
      el.playbackRate = next;
    } catch {
      /* an element that refuses keeps the last rate */
    }
  }
  try {
    window.localStorage.setItem(READ_ALOUD_RATE_KEY, String(next));
  } catch {
    /* a viewer with no storage still gets the rate for this run */
  }
  publish({ rate: next });
}

/** The − / + buttons and the arrow keys: one ten-point step at a time. */
export function stepReadAloudRate(steps: number): void {
  setReadAloudRate(snapshot.rate + steps * READ_ALOUD_RATE_STEP);
}

/** Play one URL on the given element and resolve when it finishes, fails, or is interrupted. */
function playOn(state: ActiveRun, el: HTMLAudioElement, url: string): Promise<PlayOutcome> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (how: PlayOutcome) => {
      if (settled) return;
      settled = true;
      el.onended = null;
      el.onerror = null;
      el.ontimeupdate = null;
      // Bank this segment's time so the clock keeps climbing across the seam.
      if (how === 'ended' && Number.isFinite(el.duration) && el.duration > 0) {
        state.playedSeconds += el.duration;
      } else if (Number.isFinite(el.currentTime)) {
        state.playedSeconds += el.currentTime;
      }
      if (state.el === el) state.el = null;
      state.interrupt = null;
      resolve(how);
    };
    el.onended = () => finish('ended');
    el.onerror = () => finish('failed');
    // THE CLOCK (2026-09-10). The reader is told how far along the voice is and
    // how long it has been talking — the two things a person actually wants
    // from a player — instead of which of our internal chunks is sounding.
    // Progress spans the WHOLE readout: whole segments already done, plus how
    // far through this one, over the total. `timeupdate` fires ~4x a second,
    // which is smooth enough for a bar and cheap enough for a long transcript.
    el.ontimeupdate = () => {
      if (state.run !== generation) return;
      const total = Math.max(1, state.total);
      const duration = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : 0;
      const within = duration ? Math.min(1, Math.max(0, el.currentTime / duration)) : 0;
      publish({
        progress: Math.min(1, (state.index + within) / total),
        elapsedSeconds: state.playedSeconds + (Number.isFinite(el.currentTime) ? el.currentTime : 0),
      });
    };
    state.el = el;
    state.interrupt = (how: PlayOutcome) => {
      try {
        el.pause();
      } catch {
        /* pausing a dead element is not a failure */
      }
      finish(how);
    };
    try {
      // An element PRIMED with this url already holds its bytes — assigning the
      // same src again would throw the buffer away and re-download it.
      if (el.src !== url) {
        el.src = url;
      } else {
        try {
          el.currentTime = 0;
        } catch {
          /* a fresh element has no timeline yet; it starts at 0 anyway */
        }
      }
      try {
        el.playbackRate = snapshot.rate;
      } catch {
        /* an element that refuses the rate still plays at 1 */
      }
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => finish('failed'));
    } catch {
      finish('failed');
    }
  });
}

/**
 * A refusal the SERVER made on purpose — no permission, the daily ceiling, an
 * empty wallet, nothing to read. These are answers, so the reader is told; the
 * device voice is not wheeled out to paper over them.
 */
function isDeliberateRefusal(reason: string | undefined): boolean {
  return reason === 'no-permission'
    || reason === 'not-a-participant'
    || reason === 'ceiling'
    || reason === 'out-of-work-units'
    || reason === 'no-text'
    || reason === 'disabled';
}

/**
 * Start, or stop if this message is already being read.
 *
 * MUST be called from the click handler itself (it primes audio synchronously).
 */
export function toggleReadAloud(target: ReadAloudTarget): void {
  const key = readAloudKey(target.threadId, target.messageId);
  if (snapshot.key === key) {
    stopReadAloud();
    return;
  }
  unlock();
  generation += 1;
  const run = generation;
  active?.interrupt?.('stopped');
  active = null;
  halt();
  publish({ key, state: 'loading', segment: 0, segmentCount: 0, progress: 0, elapsedSeconds: 0, preparingNext: false, notice: null, noticeKey: null });
  void drive(run, key, target);
}

/** This run's copy of a segment: the cache first, the server only when it must. */
async function segmentFor(state: ActiveRun, index: number): Promise<ReadAloudSegment | null> {
  const cached = state.cache.get(index);
  if (cached) return cached;
  const already = state.inFlight.get(index);
  if (already) return already;
  const request = fetchSegmentOnce(state, index);
  state.inFlight.set(index, request);
  try {
    return await request;
  } finally {
    state.inFlight.delete(index);
  }
}

async function fetchSegmentOnce(state: ActiveRun, index: number): Promise<ReadAloudSegment | null> {
  const response = await fetchReadAloudSegment({
    threadId: state.target.threadId,
    messageId: state.target.messageId,
    segment: index,
    fallbackText: state.target.text.slice(0, 2000),
    language: state.target.language,
  });
  if (state.run !== generation) return null;
  if (response.segmentCount) state.total = Math.max(state.total, response.segmentCount);
  if (!response.ok || !response.segment) return null;
  state.cache.set(index, response.segment);
  return response.segment;
}

/**
 * Fetch the NEXT segment while this one plays and hand its url to the idle
 * element straight away, so its bytes are downloading before the reader can
 * hear the seam. `load()` is what starts that download.
 */
function primeAhead(state: ActiveRun, index: number, el: HTMLAudioElement | undefined): void {
  if (!el || index >= state.total) return;
  void segmentFor(state, index).then((segment) => {
    if (!segment || state.run !== generation) return;
    if (state.el === el) return; // it started sounding on this element already
    try {
      if (el.src !== segment.url) {
        el.src = segment.url;
        el.load();
      }
    } catch {
      /* priming is an optimisation; the play path assigns it again if needed */
    }
  });
}

/**
 * How many segments ahead the RENDER is kept running.
 *
 * One was not enough (Miles, 2026-09-11: "awkward wait, proceeds and stop and
 * proceeds and stop"). A segment's render is only started when the segment
 * before it begins sounding, so the whole cover for it is that one segment's
 * playing time — and production renders take 5–19 s. Any segment whose render
 * runs long starves the seam after it, and because the look-ahead never got
 * further ahead, the next seam starts from behind too: the stutter repeats
 * instead of settling. Two deep means a render has the previous TWO segments'
 * playing time to finish in, so one slow render is absorbed rather than heard.
 *
 * It costs nothing extra: every segment is rendered exactly once either way
 * (`segmentFor` dedupes), and this only moves that work earlier.
 */
const READ_ALOUD_LOOKAHEAD = 2;

/**
 * Keep the pipeline full: put the next segment's bytes on the idle element, and
 * start the renders further ahead so they are not being waited on at the seam.
 */
function warmAhead(state: ActiveRun, from: number, els: HTMLAudioElement[]): void {
  primeAhead(state, from, els[from % els.length]);
  for (let i = from + 1; i < from + READ_ALOUD_LOOKAHEAD; i += 1) {
    if (i >= state.total) break;
    // No element to park it on — this only gets the render running, so the
    // bytes exist by the time it IS the next segment.
    void segmentFor(state, i);
  }
}

async function drive(run: number, key: string, target: ReadAloudTarget): Promise<void> {
  const els = audioElements();
  const state: ActiveRun = {
    run,
    key,
    target,
    total: 1,
    index: 0,
    cache: new Map(),
    el: null,
    interrupt: null,
    retried: new Set(),
    playedSeconds: 0,
    inFlight: new Map(),
  };
  active = state;

  const opening = await fetchReadAloudSegment({
    threadId: target.threadId,
    messageId: target.messageId,
    segment: 0,
    fallbackText: target.text.slice(0, 2000),
    language: target.language,
  });
  if (run !== generation) return;

  if (!opening.ok || !opening.segment) {
    active = null;
    if (isDeliberateRefusal(opening.reason)) {
      publish({ key: null, state: 'idle', notice: opening.notice || null, noticeKey: key });
      return;
    }
    // The voice could not be had. Rather than dead-end, read it here.
    if (deviceVoiceAvailable() && target.text.trim()) {
      publish({ state: 'playing', segmentCount: 1 });
      await speakWithDeviceVoice(target.text, target.language);
      if (run !== generation) return;
      publish({ key: null, state: 'idle', segment: 0, segmentCount: 0, progress: 0, elapsedSeconds: 0, preparingNext: false });
      return;
    }
    publish({ key: null, state: 'idle', notice: opening.notice || null, noticeKey: key });
    return;
  }

  state.total = Math.max(1, opening.segmentCount || 1);
  state.cache.set(0, opening.segment);
  publish({ state: 'playing', segment: 0, segmentCount: state.total });

  // THE NEXT PART STARTS BEING MADE NOW, not when the opening starts sounding
  // (2026-09-11). The opening is deliberately short so the first word arrives
  // quickly, which leaves very little playing time to cover the next render —
  // so every second of head start counts, and the seconds between this line and
  // the first note of audio were being thrown away. Deduped in `segmentFor`, so
  // the loop's own look-ahead below joins this request rather than paying for a
  // second render of the same words.
  if (state.total > 1) warmAhead(state, 1, els);

  while (run === generation) {
    const index = state.index;
    // Ready audio is handed over without a word; only a real wait is announced.
    const waiting = !state.cache.has(index);
    if (waiting && index > 0) publish({ preparingNext: true });
    const segment = await segmentFor(state, index);
    if (run !== generation) return;
    if (waiting && index > 0) publish({ preparingNext: false });
    if (!segment) {
      if (!state.retried.has(index)) {
        state.retried.add(index);
        state.cache.delete(index);
        continue;
      }
      break;
    }

    const el = els[index % els.length];
    publish({ segment: index, segmentCount: state.total, state: snapshot.state === 'paused' ? 'playing' : snapshot.state });
    // Start the next fetch BEFORE waiting on this segment to finish playing, and
    // put its url on the other element — this overlap is what keeps a long
    // readout gapless.
    warmAhead(state, index + 1, els);

    const how = await playOn(state, el, segment.url);
    if (run !== generation) return;
    if (how === 'stopped') return;
    if (how === 'seek') continue;
    if (how === 'failed') {
      // A signed url can go stale and a socket can drop; ask for it once more
      // before telling the reader the voice is gone.
      if (!state.retried.has(index)) {
        state.retried.add(index);
        state.cache.delete(index);
        continue;
      }
      break;
    }
    if (index + 1 >= state.total) {
      active = null;
      publish({ key: null, state: 'idle', segment: 0, segmentCount: 0, progress: 0, elapsedSeconds: 0, preparingNext: false });
      return;
    }
    state.index = index + 1;
  }

  if (run !== generation) return;
  active = null;
  halt();
  publish({
    key: null,
    state: 'idle',
    segment: 0,
    segmentCount: 0,
    notice: readAloudPlaybackFailedNotice(target.language),
    noticeKey: key,
  });
}

/** Test seam: forget the elements and the unlock between cases. */
export function resetReadAloudPlayerForTests(): void {
  generation += 1;
  elements = [];
  unlocked = false;
  active = null;
  snapshot = { ...IDLE, rate: storedRate() };
  listeners.clear();
}
